import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SimpleAlert } from "../components/Alerts/Alerts";
import { useTranslationApp } from "../lib/i18next";
import {
  WHITE_LABEL_CHECKOUT_CANCELLED,
  WHITE_LABEL_CONFIRMING_PAYMENT,
} from "../i18n/keysTranslations";
import { ALERT_ICON_TYPE_INFO } from "../utils/constants";
import {
  WHITE_LABEL_CHECKOUT_POLL_MS,
  WHITE_LABEL_CHECKOUT_TIMEOUT_MS,
  readWhiteLabelCheckoutResult,
  shouldOpenWhiteLabelBranding,
  stripWhiteLabelCheckoutParams,
} from "../utils/whiteLabelAddon";

const CHECKOUT_RETURN_STORAGE_KEY = "adle.whiteLabelCheckoutReturn";

/** Survives a strict-mode remount after the query string is already cleaned. */
let lastHandledCheckoutQuery = null;

const readStoredCheckoutResult = () => {
  try {
    return sessionStorage.getItem(CHECKOUT_RETURN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredCheckoutResult = (value) => {
  try {
    if (value) {
      sessionStorage.setItem(CHECKOUT_RETURN_STORAGE_KEY, value);
      return;
    }
    sessionStorage.removeItem(CHECKOUT_RETURN_STORAGE_KEY);
  } catch {
    // Private mode or disabled storage. The query guard still runs once.
  }
};

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * `/billing?featureAddon=success|cancel&addonKey=whiteLabel`.
 * Success polls status until the add-on is active, then opens branding.
 */
const useWhiteLabelCheckoutReturn = ({
  enabled = true,
  status,
  refreshStatus,
  onOpenBranding,
}) => {
  const { t } = useTranslationApp();
  const [params, setParams] = useSearchParams();
  const [phase, setPhase] = useState(null);
  const openedRef = useRef(false);
  const onOpenBrandingRef = useRef(onOpenBranding);
  onOpenBrandingRef.current = onOpenBranding;

  useEffect(() => {
    if (!enabled) return undefined;
    const query = params.toString();
    const fromUrl = readWhiteLabelCheckoutResult(params);

    if (!fromUrl) {
      lastHandledCheckoutQuery = null;
    } else if (lastHandledCheckoutQuery !== query) {
      lastHandledCheckoutQuery = query;
      if (fromUrl === "success") {
        writeStoredCheckoutResult("success");
        openedRef.current = false;
      }
      setParams(stripWhiteLabelCheckoutParams(params), { replace: true });
      if (fromUrl === "cancel") {
        SimpleAlert({
          title: t(WHITE_LABEL_CHECKOUT_CANCELLED),
          icon: ALERT_ICON_TYPE_INFO,
        });
        return undefined;
      }
      setPhase("polling");
      return undefined;
    }

    if (
      readStoredCheckoutResult() === "success" &&
      phase !== "polling" &&
      phase !== "done"
    ) {
      openedRef.current = false;
      setPhase("polling");
    }
    return undefined;
  }, [enabled, params, phase, setParams, t]);

  useEffect(() => {
    if (phase === "done") writeStoredCheckoutResult(null);
  }, [phase]);

  useEffect(() => {
    if (phase !== "polling") return undefined;
    if (!shouldOpenWhiteLabelBranding(status) || openedRef.current) {
      return undefined;
    }
    openedRef.current = true;
    setPhase("done");
    onOpenBrandingRef.current?.();
    return undefined;
  }, [phase, status]);

  useEffect(() => {
    if (phase !== "polling") return undefined;
    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      while (!cancelled && !openedRef.current) {
        const elapsed = Date.now() - startedAt;
        if (elapsed >= WHITE_LABEL_CHECKOUT_TIMEOUT_MS) {
          SimpleAlert({
            title: t(WHITE_LABEL_CONFIRMING_PAYMENT),
            icon: ALERT_ICON_TYPE_INFO,
          });
          setPhase("done");
          return;
        }
        const next = await refreshStatus();
        if (cancelled || openedRef.current) return;
        if (shouldOpenWhiteLabelBranding(next)) {
          openedRef.current = true;
          setPhase("done");
          onOpenBrandingRef.current?.();
          return;
        }
        await wait(WHITE_LABEL_CHECKOUT_POLL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [phase, refreshStatus, t]);
};

export default useWhiteLabelCheckoutReturn;
